// frontend/src/components/modals/SecurityConfirmModal.jsx
// Security confirmation modal for sensitive actions

import React, { useState } from 'react';
import { FiAlertTriangle, FiShield, FiX, FiTrash2, FiLogOut, FiMail } from 'react-icons/fi';

export default function SecurityConfirmModal({ action, data, onClose, onConfirm }) {
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const getActionDetails = () => {
    switch (action) {
      case 'revoke_session':
        return {
          title: 'Revoke Session',
          description: `Are you sure you want to revoke the session from ${data.device || 'Unknown Device'}?`,
          buttonText: 'Revoke Session',
          buttonClass: 'btn-danger',
          icon: FiLogOut,
          iconClass: 'icon-warning',
          requiresPassword: false,
          requiresConfirmText: false,
          confirmationText: null
        };
        
      case 'revoke_all_sessions':
        return {
          title: 'Sign Out All Devices',
          description: 'This will sign you out of all devices except this one. You\'ll need to sign in again on other devices.',
          buttonText: 'Sign Out All Devices',
          buttonClass: 'btn-danger',
          icon: FiLogOut,
          iconClass: 'icon-warning',
          requiresPassword: true,
          requiresConfirmText: false,
          confirmationText: null
        };
        
      case 'delete_account':
        return {
          title: 'Delete Account',
          description: 'This action cannot be undone. All your data, quiz history, achievements, and account information will be permanently deleted.',
          buttonText: 'Delete Account Forever',
          buttonClass: 'btn-danger',
          icon: FiTrash2,
          iconClass: 'icon-danger',
          requiresPassword: true,
          requiresConfirmText: true,
          confirmationText: 'DELETE MY ACCOUNT'
        };
        
      case 'change_email':
        return {
          title: 'Change Email Address',
          description: `Confirm your password to change your email address to ${data.newEmail || 'the new email'}.`,
          buttonText: 'Change Email',
          buttonClass: 'btn-primary',
          icon: FiMail,
          iconClass: 'icon-primary',
          requiresPassword: true,
          requiresConfirmText: false,
          confirmationText: null
        };
        
      case 'disable_2fa':
        return {
          title: 'Disable Two-Factor Authentication',
          description: 'This will remove the extra security layer from your account. Your account will be less secure without 2FA.',
          buttonText: 'Disable 2FA',
          buttonClass: 'btn-danger',
          icon: FiShield,
          iconClass: 'icon-warning',
          requiresPassword: true,
          requiresConfirmText: false,
          confirmationText: null
        };
        
      case 'export_data':
        return {
          title: 'Export Account Data',
          description: 'Confirm your password to download all your account data.',
          buttonText: 'Export Data',
          buttonClass: 'btn-primary',
          icon: FiShield,
          iconClass: 'icon-primary',
          requiresPassword: true,
          requiresConfirmText: false,
          confirmationText: null
        };
        
      default:
        return {
          title: 'Confirm Action',
          description: 'Please confirm this security-sensitive action.',
          buttonText: 'Confirm',
          buttonClass: 'btn-primary',
          icon: FiShield,
          iconClass: 'icon-primary',
          requiresPassword: false,
          requiresConfirmText: false,
          confirmationText: null
        };
    }
  };

  const details = getActionDetails();

  const handleConfirm = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Validate confirmation text if required
      if (details.requiresConfirmText && confirmText !== details.confirmationText) {
        setError(`Please type "${details.confirmationText}" to confirm`);
        setLoading(false);
        return;
      }
      
      // Pass password and confirmation data to parent
      await onConfirm({
        password: details.requiresPassword ? password : null,
        confirmText: details.requiresConfirmText ? confirmText : null,
        sessionId: data.sessionId,
        newEmail: data.newEmail
      });
      
    } catch (error) {
      setError(error.response?.data?.message || error.message || 'Action failed');
      setLoading(false);
    }
  };

  const isFormValid = () => {
    if (details.requiresPassword && !password) return false;
    if (details.requiresConfirmText && confirmText !== details.confirmationText) return false;
    return true;
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && isFormValid()) {
      handleConfirm();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div 
        className="modal-content security-confirm-modal" 
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyPress}
        tabIndex={-1}
      >
        <div className="modal-header">
          <h3>{details.title}</h3>
          <button className="modal-close" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>
        
        <div className="modal-body">
          <div className="security-icon">
            <details.icon size={48} className={details.iconClass} />
          </div>
          
          <div className="security-content">
            <p className="security-description">{details.description}</p>
            
            {/* Additional warning for destructive actions */}
            {action === 'delete_account' && (
              <div className="danger-warning">
                <div className="warning-items">
                  <h4>This will permanently delete:</h4>
                  <ul>
                    <li>All quiz results and history</li>
                    <li>All achievements and progress</li>
                    <li>Your profile and settings</li>
                    <li>Any active subscriptions</li>
                    <li>All associated data</li>
                  </ul>
                </div>
              </div>
            )}
            
            {/* Password confirmation */}
            {details.requiresPassword && (
              <div className="password-confirm">
                <label htmlFor="password-input">Confirm your password:</label>
                <input
                  id="password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoFocus={!details.requiresConfirmText}
                  autoComplete="current-password"
                />
              </div>
            )}
            
            {/* Text confirmation for destructive actions */}
            {details.requiresConfirmText && (
              <div className="text-confirm">
                <label htmlFor="confirm-input">
                  Type <strong>{details.confirmationText}</strong> to confirm:
                </label>
                <input
                  id="confirm-input"
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={details.confirmationText}
                  autoFocus={!details.requiresPassword}
                  autoComplete="off"
                />
              </div>
            )}
            
            {/* Error display */}
            {error && (
              <div className="error-message">
                <FiAlertTriangle size={16} />
                <span>{error}</span>
              </div>
            )}
            
            {/* Action buttons */}
            <div className="confirm-actions">
              <button 
                className="btn btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                className={`btn ${details.buttonClass}`}
                onClick={handleConfirm}
                disabled={loading || !isFormValid()}
              >
                {loading ? (
                  <>
                    <div className="spinner"></div>
                    Processing...
                  </>
                ) : (
                  details.buttonText
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
